import { type JSX, useMemo } from 'react';
import qr from 'qrcode-generator';

/**
 * Ein QR-Code für die Einrichtung des zweiten Faktors (SYS-05).
 *
 * ============================================================================
 * WARUM ER GEBRAUCHT WIRD
 * ============================================================================
 *
 * Beim Einrichten sitzt man am Rechner und hält das Telefon in der Hand. Das
 * Geheimnis abzutippen ist mühsam und fehlerträchtig — zweiunddreißig Zeichen,
 * bei denen ein einziger Dreher dazu führt, dass die App Codes erzeugt, die
 * nie passen, und niemand weiß warum. Der `otpauth`-Verweis daneben hilft nur,
 * wenn die Seite auf dem Telefon offen ist.
 *
 * ============================================================================
 * DREI DINGE, DIE EIN QR-CODE FALSCH MACHEN KANN
 * ============================================================================
 *
 * 1. ZU WENIG RUHEZONE. Ohne den hellen Rand von vier Modulen findet ein
 *    Lesegerät die Begrenzung nicht. Der Code ist dann gültig und trotzdem
 *    nicht lesbar — der ärgerlichste Fehler, weil er wie ein Kamerafehler
 *    aussieht.
 *
 * 2. DUNKLES THEMA. Ein QR-Code lebt vom Hell-Dunkel-Verhältnis. Auf dunklem
 *    Grund mit dunklen Modulen ist er unlesbar, invertiert lesen ihn nur
 *    manche Geräte. Er trägt deshalb IMMER seinen eigenen weissen Grund,
 *    unabhängig vom Thema der Oberfläche.
 *
 * 3. KEIN ERSATZ FÜR DIE AUGEN, DIE IHN NICHT SEHEN. Ein Bild allein schließt
 *    aus, wer nicht sieht. Das Geheimnis steht deshalb weiterhin als Text
 *    daneben; dieses Bild ist die Abkürzung, nicht der einzige Weg (NFA-08).
 */

/** Die helle Randzone in Modulen. Vier ist das Mindestmaß der Norm. */
const RUHEZONE = 4;

export function QrCode({
  inhalt,
  beschreibung,
  kantenlaenge = 200,
}: {
  readonly inhalt: string;
  /** Was hier steht, hört jemand, der das Bild nicht sieht. */
  readonly beschreibung: string;
  readonly kantenlaenge?: number;
}): JSX.Element {
  const bild = useMemo(() => {
    /*
     * Fehlerkorrektur 'M' (etwa 15 %) und automatische Version (0).
     * 'L' wäre kleiner und auf einem Bildschirm mit Reflexionen zu knapp;
     * 'Q' machte den Code dichter, ohne dass es hier etwas brächte.
     */
    const code = qr(0, 'M');
    code.addData(inhalt);
    code.make();

    const module = code.getModuleCount();
    const gesamt = module + RUHEZONE * 2;

    /*
     * EIN PFAD FÜR ALLE MODULE statt eines Rechtecks je Modul.
     *
     * Ein Code mittlerer Größe hat über tausend dunkle Module; als einzelne
     * Elemente sind das tausend DOM-Knoten für ein Bild, das sich nie
     * ändert. Als ein `d`-Attribut ist es eine Zeichenkette.
     */
    const teile: string[] = [];
    for (let zeile = 0; zeile < module; zeile += 1) {
      for (let spalte = 0; spalte < module; spalte += 1) {
        if (code.isDark(zeile, spalte)) {
          teile.push(`M${String(spalte + RUHEZONE)} ${String(zeile + RUHEZONE)}h1v1h-1z`);
        }
      }
    }
    return { pfad: teile.join(''), gesamt };
  }, [inhalt]);

  return (
    <svg
      role="img"
      aria-label={beschreibung}
      width={kantenlaenge}
      height={kantenlaenge}
      viewBox={`0 0 ${String(bild.gesamt)} ${String(bild.gesamt)}`}
      /*
       * `shapeRendering="crispEdges"` verhindert das Weichzeichnen an den
       * Modulkanten. Ohne das verwischt der Browser beim Skalieren genau die
       * Kanten, an denen ein Lesegerät die Module unterscheidet.
       */
      shapeRendering="crispEdges"
      className="qrcode"
    >
      {/* Der eigene helle Grund — auch im dunklen Thema. */}
      <rect width={bild.gesamt} height={bild.gesamt} fill="#ffffff" />
      <path d={bild.pfad} fill="#000000" />
    </svg>
  );
}
